import { EditNote, KeyboardArrowUp } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Address } from "../../model/Address";
import { AddressAutocomplete } from "./AddressAutocomplete";
interface AddressFormProps {
  current?: Address;
  required?: boolean;
  autocompleteLabel?: string;
  noValidate?: boolean;
  condensed?: boolean;
  manual?: boolean;
  noControls?: boolean;
  canExpand?: boolean;
  strict?: boolean;
  disabled?: boolean;
  autoSubmit?: boolean;
  variant?: "standard" | "filled" | "outlined";
  autocompleteSize?: "small" | "medium";
  onSubmit?: (address: Address) => void;
  onChange?: (address: Address) => void;
  onCancel?: () => void;
  onInsufficient?: () => void;
}

interface State {
  fullName: string;
  abbreviation: string;
}
const states: Array<State> = [
  { fullName: "Alabama", abbreviation: "AL" },
  { fullName: "Alaska", abbreviation: "AK" },
  { fullName: "Arizona", abbreviation: "AZ" },
  { fullName: "Arkansas", abbreviation: "AR" },
  { fullName: "California", abbreviation: "CA" },
  { fullName: "Colorado", abbreviation: "CO" },
  { fullName: "Connecticut", abbreviation: "CT" },
  { fullName: "Delaware", abbreviation: "DE" },
  { fullName: "Florida", abbreviation: "FL" },
  { fullName: "Georgia", abbreviation: "GA" },
  { fullName: "Hawaii", abbreviation: "HI" },
  { fullName: "Idaho", abbreviation: "ID" },
  { fullName: "Illinois", abbreviation: "IL" },
  { fullName: "Indiana", abbreviation: "IN" },
  { fullName: "Iowa", abbreviation: "IA" },
  { fullName: "Kansas", abbreviation: "KS" },
  { fullName: "Kentucky", abbreviation: "KY" },
  { fullName: "Louisiana", abbreviation: "LA" },
  { fullName: "Maine", abbreviation: "ME" },
  { fullName: "Maryland", abbreviation: "MD" },
  { fullName: "Massachusetts", abbreviation: "MA" },
  { fullName: "Michigan", abbreviation: "MI" },
  { fullName: "Minnesota", abbreviation: "MN" },
  { fullName: "Mississippi", abbreviation: "MS" },
  { fullName: "Missouri", abbreviation: "MO" },
  { fullName: "Montana", abbreviation: "MT" },
  { fullName: "Nebraska", abbreviation: "NE" },
  { fullName: "Nevada", abbreviation: "NV" },
  { fullName: "New Hampshire", abbreviation: "NH" },
  { fullName: "New Jersey", abbreviation: "NJ" },
  { fullName: "New Mexico", abbreviation: "NM" },
  { fullName: "New York", abbreviation: "NY" },
  { fullName: "North Carolina", abbreviation: "NC" },
  { fullName: "North Dakota", abbreviation: "ND" },
  { fullName: "Ohio", abbreviation: "OH" },
  { fullName: "Oklahoma", abbreviation: "OK" },
  { fullName: "Oregon", abbreviation: "OR" },
  { fullName: "Pennsylvania", abbreviation: "PA" },
  { fullName: "Rhode Island", abbreviation: "RI" },
  { fullName: "South Carolina", abbreviation: "SC" },
  { fullName: "South Dakota", abbreviation: "SD" },
  { fullName: "Tennessee", abbreviation: "TN" },
  { fullName: "Texas", abbreviation: "TX" },
  { fullName: "Utah", abbreviation: "UT" },
  { fullName: "Vermont", abbreviation: "VT" },
  { fullName: "Virginia", abbreviation: "VA" },
  { fullName: "Washington", abbreviation: "WA" },
  { fullName: "West Virginia", abbreviation: "WV" },
  { fullName: "Wisconsin", abbreviation: "WI" },
  { fullName: "Wyoming", abbreviation: "WY" },
];

/**
 * A reusable form used to retrieve an address from the user.
 *
 * Methods to retrieve address:
 * @param onSubmit Returns the new address from the user when they press submit.
 * @param onChange Returns the new address from the user every time they update.
 * @param onCancel Enables the cancel button and assigns functionality.
 * @param onInsufficient This is called when the autocomplete selects an address that has no matching google place.
 *
 * Properties:
 * @param current Autofills the form with the set address.
 * @param autocompleteLabel The label for the autocomplete
 * @param noValidate Disables validation preventing sumission of the form.
 * @param condensed Disables the manual form.
 * @param manual Disables the autocomplete form.
 * @param canExpand Enables the ability for the user to exit condensed mode
 * @param noControls Hides the control buttons.
 * @param autoSubmit Setting to true sets form to submit as soon as autocomplete sends a value
 * @param strict Disables the ability to search for address by business name
 * @param required Does not effect functionality, just adds the asterisk.
 * @param disabled Disables the address form if set to true.
 */
const AddressForm: FC<AddressFormProps> = ({
  variant,
  current,
  onChange,
  ...props
}) => {
  const [address, setAddress] = useState<Address | undefined>(current);
  const [errorText, setErrorText] = useState<string>("");
  const [stateValue, setStateValue] = useState<State | null>(
    states.find((st) => st.abbreviation === address?.state) || null,
  );
  const [stateText, setStateText] = useState<string>(
    states.find((st) => st.abbreviation === address?.state)?.fullName || "",
  );
  const [condensedCurrent, setCondensedCurrent] = useState<boolean>(
    props.condensed || false,
  );

  const empty = {
    id: "",
    first_line: "",
    second_line: "",
    city: "",
    state: "",
    zip_code: 0,
  };

  useEffect(() => {
    setCondensedCurrent(props.condensed || false);
  }, [props.condensed]);

  useEffect(() => {
    if (current) onChange?.(current);
  }, [current, onChange]);

  const onSubmit = () => {
    setErrorText("");
    let canSubmit = true;

    const firstLineRegex = /^[A-Za-z0-9][A-Za-z0-9 ]*$/;
    const cityStateRegex = /^[A-Za-z0-9 ]+$/;
    const zipCodeRegex = /^\d+$/;

    if (!address) canSubmit = false;
    if (!props.noValidate && canSubmit && address) {
      if (
        !firstLineRegex.test(address.first_line) ||
        address.first_line == ""
      ) {
        canSubmit = false;
        setErrorText("Cannot submit address: First line is invalid.");
      }
      if (!cityStateRegex.test(address.state) || address.state == "") {
        canSubmit = false;
        setErrorText("Cannot submit address: State is invalid.");
      }
      if (!cityStateRegex.test(address.city) || address.city == "") {
        canSubmit = false;
        setErrorText("Cannot submit address: City is invalid.");
      }
      if (
        !zipCodeRegex.test(address.zip_code.toString()) ||
        address.zip_code == 0
      ) {
        canSubmit = false;
        setErrorText("Cannot submit address: Zip code is invalid.");
      }
    }
    if (props.onSubmit && address && canSubmit) props.onSubmit(address);
  };

  // On form change, validates and submit automatically
  const autoSubmit = () => {
    if (
      !address?.first_line ||
      address.first_line == "" ||
      !address.city ||
      address.city == "" ||
      !address.state ||
      address.state == "" ||
      !address.zip_code ||
      address.zip_code == 0
    ) {
      return;
    }

    if (props.onSubmit) props.onSubmit(address);
  };

  const onCancel = () => {
    props.onCancel?.();
  };

  // if address is undefined, set to blank
  useEffect(() => {
    if (!address) {
      setAddress({
        id: "",
        first_line: "",
        second_line: "",
        city: "",
        state: "",
        zip_code: 0,
      });
    }
  }, [address]);

  return (
    <>
      <Grid container p={0} spacing={1} direction={"row"}>
        <Grid item xs={12}>
          {!props.manual && (
            <Grid
              item
              xs={12}
              container
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Grid item flexGrow={1}>
                {props.disabled ? (
                  <TextField
                    required={props.required}
                    color="primary"
                    size={props.autocompleteSize}
                    label={props.autocompleteLabel}
                    variant={variant || "outlined"}
                    fullWidth
                  />
                ) : (
                  <AddressAutocomplete
                    strict={props.strict}
                    size={props.autocompleteSize}
                    required={props.required}
                    label={props.autocompleteLabel}
                    variant={variant}
                    onInsufficient={props.onInsufficient}
                    onSubmit={(address) => {
                      if (!address && !condensedCurrent) return;
                      if (!address) {
                        setAddress(empty);
                        setStateText("");
                        setStateValue(null);
                        if (onChange) onChange(empty);
                        if (props.autoSubmit) autoSubmit();
                        return;
                      }
                      setAddress(address);
                      if (onChange && !props.autoSubmit) onChange(address);
                      if (props.onSubmit && props.autoSubmit)
                        props.onSubmit(address);

                      const state: State | undefined = states.find((state) =>
                        state.abbreviation
                          .toLowerCase()
                          .match(address.state.toLowerCase()),
                      );
                      setStateValue(state ? state : null);
                      setStateText(state?.fullName || "");
                    }}
                    value={address}
                  />
                )}
              </Grid>
              {props.canExpand && (
                <Grid
                  item
                  xs={1}
                  minWidth={"55px"}
                  container
                  flexDirection={"column"}
                  alignItems="center"
                >
                  <Tooltip
                    title={
                      condensedCurrent
                        ? "Manually update address"
                        : "Hide address form"
                    }
                    sx={{
                      transform:
                        variant === "outlined" ||
                        variant === "filled" ||
                        !variant
                          ? "translateY(11px)"
                          : " ",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setCondensedCurrent(!condensedCurrent)}
                    >
                      {condensedCurrent ? <EditNote /> : <KeyboardArrowUp />}
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
        {!condensedCurrent && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required={props.required}
                value={address?.first_line || ""}
                size="small"
                id="line-one"
                label="Address Line One"
                variant="filled"
                onChange={(event) => {
                  if (address) {
                    const updatedValue = event.target.value;
                    setAddress((prevAddress) =>
                      prevAddress
                        ? { ...prevAddress, first_line: updatedValue }
                        : undefined,
                    );
                    if (onChange)
                      onChange({ ...address, first_line: updatedValue });
                    if (props.autoSubmit) autoSubmit();
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                value={address?.second_line || ""}
                size="small"
                id="line-two"
                label="Address Line Two"
                variant="filled"
                onChange={(event) => {
                  if (address) {
                    const updatedValue = event.target.value;
                    setAddress((prevAddress) =>
                      prevAddress
                        ? { ...prevAddress, second_line: updatedValue }
                        : undefined,
                    );
                    if (onChange)
                      onChange({ ...address, second_line: updatedValue });
                    if (props.autoSubmit) autoSubmit();
                  }
                }}
              />
            </Grid>
            <Grid container item spacing={1} rowSpacing={0}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  required={props.required}
                  value={address?.city || ""}
                  size="small"
                  id="city"
                  label="City"
                  variant="filled"
                  onChange={(event) => {
                    if (address) {
                      const updatedValue = event.target.value;
                      setAddress((prevAddress) =>
                        prevAddress
                          ? { ...prevAddress, city: updatedValue }
                          : undefined,
                      );
                      if (onChange)
                        onChange({ ...address, city: updatedValue });
                      if (props.autoSubmit) autoSubmit();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  value={stateValue}
                  inputValue={stateText || ""}
                  renderInput={(params) => (
                    <TextField
                      variant="filled"
                      label="State"
                      required={props.required}
                      {...params}
                      size="small"
                    />
                  )}
                  options={states}
                  renderOption={(props, option) => {
                    return (
                      <Box {...props} key={option.abbreviation} component="li">
                        {option.fullName}
                      </Box>
                    );
                  }}
                  getOptionLabel={(option) => option.fullName}
                  filterOptions={(options, { inputValue }) =>
                    options.filter(
                      (option) =>
                        option.fullName
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()) ||
                        option.abbreviation
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()),
                    )
                  }
                  onInputChange={(_, newValue, reason) => {
                    if (reason == "clear") setStateText("");
                    else if (reason == "input") setStateText(newValue);
                    else if (reason == "reset") setStateText("");
                  }}
                  onChange={(_, newValue) => {
                    if (address && newValue) {
                      setAddress({ ...address, state: newValue.abbreviation });
                      setStateText(newValue.fullName);
                      if (onChange) onChange(address);
                      if (props.autoSubmit) autoSubmit();
                    }
                  }}
                  onBlur={() => {
                    if (address && stateText != "") {
                      const inputValue = stateText.toLowerCase();
                      const closestMatch = states.find(
                        (state) =>
                          state.fullName.toLowerCase().includes(inputValue) ||
                          state.abbreviation.toLowerCase().includes(inputValue),
                      );
                      if (closestMatch) {
                        setAddress({
                          ...address,
                          state: closestMatch.abbreviation,
                        });
                        setStateText(closestMatch.fullName);
                        setStateValue(closestMatch);
                        if (onChange)
                          onChange({
                            ...address,
                            state: closestMatch.abbreviation,
                          });
                      }
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.abbreviation === value.abbreviation &&
                    option.fullName === value.fullName
                  }
                ></Autocomplete>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  required={props.required}
                  inputMode="numeric"
                  inputProps={{ type: "number" }}
                  value={`${
                    address && address?.zip_code == 0 ? "" : address?.zip_code
                  }`}
                  size="small"
                  id="zip"
                  label="Zip Code"
                  variant="filled"
                  onChange={(event) => {
                    if (address) {
                      setAddress({ ...address, zip_code: +event.target.value });
                      if (onChange)
                        onChange({
                          ...address,
                          zip_code: +event.target.value,
                        });
                      if (props.autoSubmit) autoSubmit();
                    }
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
        <Grid item>
          <Typography color={"error"}>{errorText}</Typography>
        </Grid>
        {!props.noControls && (
          <Grid item container spacing={1} xs={12}>
            <Grid item>
              <Button
                disabled={
                  address?.first_line == "" ||
                  address?.city == "" ||
                  address?.state == "" ||
                  address?.zip_code == 0
                }
                onClick={onSubmit}
                variant="contained"
              >
                Submit
              </Button>
            </Grid>
            {props.onCancel && (
              <Grid item>
                <Button onClick={onCancel} variant="outlined">
                  Cancel
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default AddressForm;
